import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Power, PowerOff, Trash2 } from "lucide-react";
import { http } from "@/lib/api";
import { fmtDate, shortId } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function StreamManageModal({ userId, userName, offerId, offerLabel, open, onClose, onChanged }) {
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [delText, setDelText] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["user-offer-streams", userId, offerId],
    queryFn: async () => (await http.get(`/offers/user-streams/${userId}/${offerId}`)).data,
    enabled: open && !!userId && !!offerId,
  });
  const rows = data?.items || [];
  const allSelected = rows.length > 0 && sel.size === rows.length;

  const toggle = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSel(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const selRows = useMemo(() => rows.filter((r) => sel.has(r.id)), [rows, sel]);
  const selClicks = selRows.reduce((a, r) => a + (r.clicks || 0), 0);
  const selConvs = selRows.reduce((a, r) => a + (r.conversions || 0), 0);

  const run = async (action) => {
    if (!sel.size) return toast.error("Select at least one stream");
    setBusy(true);
    try {
      const res = await http.post(`/offers/user-streams/${userId}/${offerId}/action`,
        { action, stream_ids: [...sel] });
      const d = res.data;
      if (action === "delete") toast.success(`Deleted ${d.deleted_streams} stream(s), ${d.deleted_clicks} clicks, ${d.deleted_conversions} conversions`);
      else toast.success(`${action === "activate" ? "Activated" : "Deactivated"} ${d.affected} stream(s)`);
      setSel(new Set()); setConfirmDel(false); setDelText("");
      await refetch();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const th = "px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70";
  const td = "px-2 py-2 text-xs";

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-4xl border-white/[0.1] bg-[#111114]" data-testid="stream-manage-modal">
          <DialogHeader>
            <DialogTitle>Streams · {userName}</DialogTitle>
            <DialogDescription>{offerLabel}</DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2 border-y border-white/[0.08] py-2.5">
            <Button size="sm" variant="outline" onClick={toggleAll} data-testid="stream-select-all" className="h-8 border-white/[0.12]">
              {allSelected ? "Clear selection" : "Select all"}
            </Button>
            <span className="text-xs text-muted-foreground">{sel.size} selected</span>
            <div className="ml-auto flex items-center gap-2">
              <Button size="sm" variant="outline" disabled={!sel.size || busy} onClick={() => run("activate")}
                data-testid="stream-activate" className="h-8 border-white/[0.12] text-[#22C55E]"><Power className="mr-1 h-3.5 w-3.5" />Activate</Button>
              <Button size="sm" variant="outline" disabled={!sel.size || busy} onClick={() => run("deactivate")}
                data-testid="stream-deactivate" className="h-8 border-white/[0.12] text-[#EAB308]"><PowerOff className="mr-1 h-3.5 w-3.5" />Deactivate</Button>
              <Button size="sm" variant="outline" disabled={!sel.size || busy} onClick={() => setConfirmDel(true)}
                data-testid="stream-delete" className="h-8 border-red-500/30 text-red-400 hover:bg-red-500/10"><Trash2 className="mr-1 h-3.5 w-3.5" />Delete</Button>
            </div>
          </div>

          <div className="max-h-[52vh] overflow-auto rounded-lg border border-white/[0.08]">
            {isLoading ? <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No streams for this offer.</p>
            ) : (
              <table className="w-full min-w-[820px]">
                <thead className="sticky top-0 border-b border-white/[0.08] bg-[#16161a]">
                  <tr>
                    <th className={th}><Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="stream-head-check" /></th>
                    <th className={th}>Stream</th><th className={th}>Key</th><th className={th}>Status</th>
                    <th className={th}>Landing</th><th className={th}>Clicks</th><th className={th}>Leads</th>
                    <th className={th}>Deps</th><th className={th}>Rejects</th><th className={th}>Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.05]">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-white/[0.02]" data-testid={`stream-row-${r.id}`}>
                      <td className={td}><Checkbox checked={sel.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`stream-check-${r.id}`} /></td>
                      <td className={td}><span className="font-medium text-foreground">#{r.tenant_seq} {r.name || shortId(r.id)}</span></td>
                      <td className={`${td} font-mono text-muted-foreground`}>{r.stream_key}</td>
                      <td className={td}>
                        {(() => {
                          const manualOff = !r.is_active;
                          const offerDown = (r.offer_status || "active") !== "active";
                          const ok = r.is_active && !offerDown;
                          return (
                            <span title={manualOff ? "Disabled manually" : offerDown ? "Offer disabled" : ""}
                              className={`rounded px-1.5 py-0.5 text-[10px] ${ok ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-[#EAB308]/15 text-[#EAB308]"}`}>
                              {ok ? "Active" : offerDown && !manualOff ? "Disabled — Offer disabled" : "Disabled"}
                            </span>
                          );
                        })()}
                      </td>
                      <td className={`${td} text-muted-foreground`}>{r.landing || "—"}</td>
                      <td className={`${td} font-mono`}>{r.clicks}</td>
                      <td className={`${td} font-mono text-[#3B82F6]`}>{r.leads}</td>
                      <td className={`${td} font-mono text-[#22C55E]`}>{r.deposits}</td>
                      <td className={`${td} font-mono text-red-400`}>{r.rejects}</td>
                      <td className={`${td} text-muted-foreground`}>{fmtDate(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={(o) => { if (!o) { setConfirmDel(false); setDelText(""); } }}>
        <AlertDialogContent className="border-white/[0.1] bg-[#111114]" data-testid="stream-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400">Delete {sel.size} stream(s)?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p>You are deleting <b className="text-foreground">{sel.size} stream(s)</b>, <b className="text-foreground">{selClicks} clicks</b> and <b className="text-foreground">{selConvs} conversions</b> for {userName} on {offerLabel}.</p>
                <p className="text-red-400/90">This permanently removes all related statistics for all time and cannot be undone. Overrides, the user and the offer are NOT affected.</p>
                <div>
                  <p className="mb-1 text-xs">Type <b className="text-foreground">DELETE</b> to confirm:</p>
                  <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="DELETE"
                    data-testid="stream-delete-input" className="h-9 border-white/[0.12] bg-[#0A0A0A]" />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="stream-delete-cancel" className="border-white/[0.12]">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={delText !== "DELETE" || busy} onClick={(e) => { e.preventDefault(); run("delete"); }}
              data-testid="stream-delete-final" className="bg-red-600 text-white hover:bg-red-700">
              {busy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
